// Shared helpers used across the dashboard. Mirrors stratum-analytical's
// `utils/` + `helpers.ts` conventions.
import { useState, useEffect, useCallback } from 'react';
import { CHECKLIST_STEPS } from '../data';

export const isDone = (appt) => appt.checks.every(Boolean);

export function getNextStep(appt) {
  for (let i = 0; i < CHECKLIST_STEPS.length; i++) {
    const step = CHECKLIST_STEPS[i];
    if (step.toLowerCase() === 'photos') continue;
    if (!appt.checks[i]) return { step, idx: i };
  }
  return null;
}

export function getPhotoIdentity(appt) {
  return {
    idBooking: appt.squareId || appt.id,
    customerName: appt.car || appt.customer || 'Unknown',
    serviceLabel: appt.service || 'Detail Service',
  };
}

export function toDateStr(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

export function formatDisplayDate(dateStr) {
  const d         = new Date(dateStr + 'T12:00:00');
  const today     = toDateStr(new Date());
  const tomorrow  = toDateStr(new Date(Date.now() + 86400000));
  const yesterday = toDateStr(new Date(Date.now() - 86400000));
  if (dateStr === today)     return 'Today';
  if (dateStr === tomorrow)  return 'Tomorrow';
  if (dateStr === yesterday) return 'Yesterday';
  return d.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
}

// Light/dark theme preference, persisted to localStorage under `lightMode` and
// shared across pages (dashboard + staff). `toggleLightMode` accepts a value or
// a functional updater, matching the dashboard's existing usage.
export function useLightMode() {
  const [lightMode, setLightMode] = useState(() => {
    try {
      const raw = localStorage.getItem('lightMode');
      if (!raw) return false;
      const { value } = JSON.parse(raw);
      return value;
    } catch { return false; }
  });

  const toggleLightMode = useCallback((updater) => {
    setLightMode(prev => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      try { localStorage.setItem('lightMode', JSON.stringify({ value: next })); } catch {}
      return next;
    });
  }, []);

  return [lightMode, toggleLightMode];
}

export function usePagination(items, pageSize = 10) {
  const [page, setPage] = useState(1);
  const pageCount = Math.max(1, Math.ceil(items.length / pageSize));
  const safePage = Math.min(page, pageCount);
  const start = (safePage - 1) * pageSize;
  const pageItems = items.slice(start, start + pageSize);
  const goToPage = useCallback((p) => setPage(Math.max(1, p)), []);
  return { page: safePage, pageCount, pageItems, setPage: goToPage };
}

export function useClock() {
  const [time, setTime] = useState('');
  useEffect(() => {
    const tick = () => setTime(new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }));
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);
  return time;
}
